import { createAsyncThunk } from '@reduxjs/toolkit';

import { CareerPath } from '@backrs_ui/common-ui';

import {
  CareerPathEnjoyment,
  CareerPathMeaning,
  CareerPathRole,
  CareerPathWithScore,
  CareerQuestion,
  CareerUserResponseSet,
} from '@backrs_ui/grow-ui/src/models/models';
import {
  ScoreCalculationResult,
  calculateSurveyResults,
  determineTierFromResults,
} from '@backrs_ui/grow-ui/src/utils/calculateSurveyResults';
import { getCareerPathScores } from '@backrs_ui/grow-ui/src/utils/careerPath/careerPathCalculator';
import { fetchWrapper as fetch } from '@backrs_ui/grow-ui/src/utils/fetch/fetchWrapper';
import { getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

const ENVIRONMENT_URL = process.env.REACT_APP_API_BASEURL;

type PostAPISurveyResultsInput = Readonly<{
  userResponseSet: CareerUserResponseSet;
  careerQuestions: CareerQuestion[];
  careerPaths: CareerPath[];
  careerPathRoles: CareerPathRole[];
  careerPathMeanings: CareerPathMeaning[];
  careerPathEnjoyments: CareerPathEnjoyment[];
}>;

const getSurveyResults = ({
  userResponseSet,
  careerQuestions,
  careerPaths,
  careerPathRoles,
  careerPathMeanings,
  careerPathEnjoyments,
}: PostAPISurveyResultsInput) => {
  const careerResults: ScoreCalculationResult = calculateSurveyResults(
    userResponseSet,
    careerQuestions,
  );

  const tier = determineTierFromResults(careerResults);

  const careerPathSuggestions: CareerPathWithScore[] = getCareerPathScores({
    tier,
    careerPaths,
    careerPathRoles,
    userResponseSet,
    careerResults,
    questions: careerQuestions,
    careerPathMeanings,
    careerPathEnjoyments,
  });

  return { careerResults, tier, careerPathSuggestions };
};

export const postAPISurveyResults = createAsyncThunk(
  'userResponse/postAPISurveyResults',
  async (
    {
      userResponseSet,
      careerQuestions,
      careerPaths,
      careerPathRoles,
      careerPathMeanings,
      careerPathEnjoyments,
    }: PostAPISurveyResultsInput,
    { rejectWithValue },
  ) => {
    try {
      const { careerResults, tier, careerPathSuggestions } = getSurveyResults({
        userResponseSet,
        careerQuestions,
        careerPaths,
        careerPathRoles,
        careerPathMeanings,
        careerPathEnjoyments,
      });

      const accessToken = getBackrsToken();

      // if we have an access token, we know we're logged in and can persist the results to the DB
      if (accessToken) {
        // map the career paths to the format the API expects (career_path_id, sum)
        const mappedCareerPaths = careerPathSuggestions.map((careerPath) => ({
          career_path_id: careerPath.careerPath.id,
          sum: careerPath.sum,
          ranking: careerPath.scoreSection,
        }));

        const response = await fetch(`${ENVIRONMENT_URL}/career-user-response-set`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            user_response_set: userResponseSet,
            tier,
            entrepreneurship_recommended: careerResults.entrepreneurship,
            career_path_results: mappedCareerPaths,
          }),
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const responseSet = (await response.json()) as CareerUserResponseSet;

        return {
          userResponseSet: responseSet, // update the store with the response set from the API
          results: { careerResults, tier, careerPathSuggestions },
        };
      }

      // if we don't have an access token, we're not persisting to the DB so we can just re-save the current state
      return {
        userResponseSet,
        results: { careerResults, tier, careerPathSuggestions },
      };
    } catch (error) {
      return rejectWithValue(`Failed to post survey results with error: ${error}`);
    }
  },
);

type GetUserResponseSetFromAPIInput = Readonly<{
  userResponseSetId: string;
  careerQuestions: CareerQuestion[];
  careerPaths: CareerPath[];
  careerPathRoles: CareerPathRole[];
  careerPathMeanings: CareerPathMeaning[];
  careerPathEnjoyments: CareerPathEnjoyment[];
}>;

/**
 * Fetches a user response set from the API and calculates the survey results.
 * We then take those results and calculate them. The reducer then listens for this action and updates the store with the results.
 */
export const getUserResponseSetFromAPI = createAsyncThunk(
  'userResponse/getUserResponseSetFromAPI',
  async (
    {
      userResponseSetId,
      careerQuestions,
      careerPaths,
      careerPathRoles,
      careerPathMeanings,
      careerPathEnjoyments,
    }: GetUserResponseSetFromAPIInput,
    { rejectWithValue },
  ) => {
    try {
      const accessToken = getBackrsToken();

      const response = await fetch(
        `${ENVIRONMENT_URL}/career-user-response-set/${userResponseSetId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) {
        const errorBody = await response.text();

        throw new Error(
          `Failed to get upload URL: ${response.status} ${response.statusText}\n${errorBody}`,
        );
      }

      const responseSet = (await response.json()) as CareerUserResponseSet;

      const { careerResults, tier, careerPathSuggestions } = getSurveyResults({
        userResponseSet: responseSet,
        careerQuestions,
        careerPaths,
        careerPathRoles,
        careerPathMeanings,
        careerPathEnjoyments,
      });

      return {
        userResponseSet: responseSet,
        results: { careerResults, tier, careerPathSuggestions },
      };
    } catch (error) {
      return rejectWithValue(`Failed to get user response set with error: ${error}`);
    }
  },
);

type GetMostResponseSetFromAPIInput = Readonly<{
  careerQuestions: CareerQuestion[];
  careerPaths: CareerPath[];
  careerPathRoles: CareerPathRole[];
  careerPathMeanings: CareerPathMeaning[];
  careerPathEnjoyments: CareerPathEnjoyment[];
}>;

/**
 * Fetches the most recent user response set from the API.
 * We then take those results and calculate them. The reducer then listens for this action and updates the store with the results.
 */
export const getMostRecentUserResponseSetFromAPI = createAsyncThunk(
  'userResponse/getMostRecentUserResponseSetFromAPI',
  async (
    {
      careerQuestions,
      careerPaths,
      careerPathRoles,
      careerPathMeanings,
      careerPathEnjoyments,
    }: GetMostResponseSetFromAPIInput,
    { rejectWithValue },
  ) => {
    try {
      const accessToken = getBackrsToken();

      const response = await fetch(`${ENVIRONMENT_URL}/career-user-response-set/most-recent`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const errorBody = await response.text();

        throw new Error(
          `Failed to get most recent response set: ${response.status} ${response.statusText}\n${errorBody}`,
        );
      }

      const responseSet = (await response.json()) as CareerUserResponseSet;

      const { careerResults, tier, careerPathSuggestions } = getSurveyResults({
        userResponseSet: responseSet,
        careerQuestions,
        careerPaths,
        careerPathRoles,
        careerPathMeanings,
        careerPathEnjoyments,
      });

      return {
        userResponseSet: responseSet,
        results: { careerResults, tier, careerPathSuggestions },
      };
    } catch (error) {
      return rejectWithValue(`Failed to get user response set with error: ${error}`);
    }
  },
);

export const postAPISurveyResultsNewUser = createAsyncThunk(
  'userResponse/postAPISurveyResultsNewUser',
  async (
    {
      userResponseSet,
      careerQuestions,
      careerPaths,
      careerPathRoles,
      careerPathMeanings,
      careerPathEnjoyments,
      first_name,
      last_name,
      email,
      role,
    }: PostAPISurveyResultsInput & {
      first_name: string;
      last_name: string;
      email: string;
      role: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const { careerResults, tier, careerPathSuggestions } = getSurveyResults({
        userResponseSet,
        careerQuestions,
        careerPaths,
        careerPathRoles,
        careerPathMeanings,
        careerPathEnjoyments,
      });

      // map the career paths to the format the API expects (career_path_id, sum)
      const mappedCareerPaths = careerPathSuggestions.map((careerPath) => ({
        career_path_id: careerPath.careerPath.id,
        sum: careerPath.sum,
        ranking: careerPath.scoreSection,
      }));

      const response = await fetch(`${ENVIRONMENT_URL}/career-user-response-set/new-lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_response_set: userResponseSet,
          tier,
          entrepreneurship_recommended: careerResults.entrepreneurship,
          career_path_results: mappedCareerPaths,
          first_name,
          last_name,
          email,
          role,
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const responseSet = (await response.json()) as { responseSet: CareerUserResponseSet };

      return {
        userResponseSet: responseSet.responseSet, // update the store with the response set from the API
        results: { careerResults, tier, careerPathSuggestions },
      };

      // if we don't have an access token, we're not persisting to the DB so we can just re-save the current state
    } catch (error) {
      return rejectWithValue(`Failed to post survey results with error: ${error}`);
    }
  },
);
